import React, { useEffect, useState } from 'react';
import { Check, PenLine } from 'lucide-react';
import { toast } from 'react-toastify';

import { Button } from '@/components/ui/button';
import { FormModal } from '@/components/ui/form-modal';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useInitDraftSitrepV2 } from 'components/DisasterGPT/hooks/useSitrepsV2';
import type { AiSpatialFilter } from 'components/DisasterGPT/utils/aiMapSpatialFilter';
import { useOptionalUscgMapContext } from 'components/MapPage/CorporateViewTabs/LayersTab/UscgMapContext';
import CreateDraftReportModal, {
  DEFAULT_DRAFT_TEMPLATE_ID,
  useDraftReportTemplates,
} from 'components/Reports/CreateDraftReportModal';
import { buildDraftSitrepPayload } from 'components/Reports/utils/buildDraftSitrepPayload';

const PREVIEW_OWNER_ID = 'create-custom-report';
const MAX_NAME_LENGTH = 200;

type Step = 'template' | 'area';

type CreateCustomReportDialogProps = {
  show: boolean;
  onHide: () => void;
  /** AOR the server falls back to when the drawn area overlaps none. */
  fallbackAorId?: string | null;
  onCreated: (sitrepId: string) => void;
};

/**
 * Creates a draft report for an area drawn on the map, which need not match an
 * AOR, incident or asset. The template is picked first, with the same picker as
 * AOR reports, then the report is named and its area drawn.
 */
const CreateCustomReportDialog: React.FC<CreateCustomReportDialogProps> = ({
  show,
  onHide,
  fallbackAorId,
  onCreated,
}) => {
  const mapCtx = useOptionalUscgMapContext();
  const templates = useDraftReportTemplates();
  const initDraftMutation = useInitDraftSitrepV2();
  const [step, setStep] = useState<Step>('template');
  const [templateId, setTemplateId] = useState(DEFAULT_DRAFT_TEMPLATE_ID);
  const [name, setName] = useState('');
  const [area, setArea] = useState<AiSpatialFilter | null>(null);
  const [isDrawing, setIsDrawing] = useState(false);

  const trimmedName = name.trim();
  const busy = initDraftMutation.isPending;
  const canCreate = Boolean(trimmedName && area) && !busy && !isDrawing;
  const templateName =
    templates.find((template) => template.id === templateId)?.name ??
    'Selected template';

  useEffect(() => {
    if (!isDrawing || !mapCtx) return undefined;
    mapCtx.registerAiDrawnAreaHandler((payload) => {
      setArea(payload.spatialFilter);
      setIsDrawing(false);
      mapCtx.setAiLayerPickMode(false);
    });
    mapCtx.registerAiDrawnAreaCancelHandler(() => {
      setIsDrawing(false);
      mapCtx.setAiLayerPickMode(false);
    });
    return () => {
      mapCtx.registerAiDrawnAreaHandler(null);
      mapCtx.registerAiDrawnAreaCancelHandler(null);
    };
  }, [isDrawing, mapCtx]);

  const showReportGeofence = mapCtx?.showReportGeofence;
  const clearReportGeofence = mapCtx?.clearReportGeofence;
  useEffect(() => {
    if (!show || !area || !showReportGeofence || !clearReportGeofence) {
      return undefined;
    }
    showReportGeofence(area, trimmedName || 'Custom area', PREVIEW_OWNER_ID);
    return () => clearReportGeofence(PREVIEW_OWNER_ID);
  }, [area, clearReportGeofence, show, showReportGeofence, trimmedName]);

  const reset = () => {
    setStep('template');
    setTemplateId(DEFAULT_DRAFT_TEMPLATE_ID);
    setName('');
    setArea(null);
    setIsDrawing(false);
  };

  const handleHide = () => {
    if (busy) return;
    if (isDrawing) mapCtx?.cancelAiAreaDraw();
    reset();
    onHide();
  };

  const startDraw = () => {
    if (!mapCtx) {
      toast.info('Open the map to draw the report area');
      return;
    }
    mapCtx.setIsMapVisible(true);
    mapCtx.setAiLayerPickMode(true);
    setIsDrawing(true);
    mapCtx.startAiAreaDraw();
  };

  const handleCreate = async () => {
    if (!area || !trimmedName) return;
    try {
      const sitrepId = await initDraftMutation.mutateAsync(
        buildDraftSitrepPayload({
          scopeName: trimmedName,
          aorId: fallbackAorId,
          templateId,
          customArea: {
            spatialFilter: area as unknown as Record<string, unknown>,
          },
        })
      );
      reset();
      onCreated(sitrepId);
    } catch (error: unknown) {
      const message =
        (error as { response?: { data?: { message?: string } } })?.response
          ?.data?.message ?? 'Failed to create custom report.';
      toast.error(message);
    }
  };

  return (
    <>
      <CreateDraftReportModal
        show={show && step === 'template'}
        onHide={handleHide}
        onConfirm={(selection) => {
          setTemplateId(selection.templateId);
          setStep('area');
        }}
        isSubmitting={false}
        preferredTemplateId={templateId}
        title="Create Custom Report"
        confirmLabel="Next"
        editConfirmLabel="Save and continue"
      />
      <FormModal
        // Hidden, not closed, while the user draws on the map behind it.
        open={show && step === 'area' && !isDrawing}
        onOpenChange={(open) => {
          if (!open) handleHide();
        }}
        title="Create Custom Report"
        description="Name the report and draw its area on the map. The report covers that area instead of an AOR, incident or asset."
        footer={
          <>
            <Button
              type="button"
              variant="outline"
              onClick={() => setStep('template')}
              disabled={busy}
            >
              Back
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={handleHide}
              disabled={busy}
            >
              Cancel
            </Button>
            <Button
              type="button"
              data-testid="create-custom-report-confirm"
              onClick={() => void handleCreate()}
              disabled={!canCreate}
            >
              {busy ? 'Creating...' : 'Create'}
            </Button>
          </>
        }
      >
        <div className="tw:space-y-4">
          <p className="tw:m-0 tw:text-sm tw:text-muted-foreground">
            Template:{' '}
            <span className="tw:font-medium tw:text-foreground">
              {templateName}
            </span>
          </p>
          <div className="tw:space-y-2">
            <Label htmlFor="custom-report-name">Report name</Label>
            <Input
              id="custom-report-name"
              value={name}
              maxLength={MAX_NAME_LENGTH}
              placeholder="e.g. Harbor approach"
              disabled={busy}
              autoFocus
              onChange={(event) => setName(event.target.value)}
            />
          </div>
          <div className="tw:space-y-2">
            <Label>Area</Label>
            <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-2">
              <Button
                type="button"
                variant={area ? 'outline' : 'secondary'}
                disabled={busy}
                onClick={startDraw}
              >
                <PenLine />
                {area ? 'Redraw area' : 'Draw area on map'}
              </Button>
              {area ? (
                <span className="tw:flex tw:items-center tw:gap-1 tw:text-sm tw:text-muted-foreground">
                  <Check className="tw:size-4 tw:text-success" />
                  Area drawn — shown on the map
                </span>
              ) : (
                <span className="tw:text-sm tw:text-muted-foreground">
                  No area drawn yet.
                </span>
              )}
            </div>
          </div>
        </div>
      </FormModal>
    </>
  );
};

export default CreateCustomReportDialog;
